/* @flow */

import type { Point, Tools } from './Common';
import { GridHeight, GoalWidth, GoalHeight } from './Common';
import GlobalManager from './GlobalManager';
import Particle from './Particle';

export default class Goal {
  position: Point;
  width = GoalWidth;
  basinHeight = GoalHeight;
  completeThreshold = 80;
  progress = 0;
  volume = 0;
  progressStep = 0.1;
  pendingProgress = [];
  acceptsColors: Array<string>;
  tracks: Array<string>;

  constructor(position: Point, acceptsColors: Array<string>, tracks: Array<string>) {
    GlobalManager.canvas.subscribeGoal(tools => this.draw(tools));
    GlobalManager.clock.subscribeGoal(() => this.step());

    this.position = position;
    this.acceptsColors = acceptsColors;
    this.tracks = tracks;
  }

  contains(p: Point) {
    const { position, width, basinHeight } = this;
    return (
      position.x - width / 2 <= p.x &&
      p.x <= position.x + width / 2 &&
      position.y <= p.y &&
      p.y < position.y + basinHeight
    );
  }

  feedParticle(particle: Particle) {
    const { acceptsColors, progressStep } = this;
    if (!acceptsColors.length || acceptsColors.includes(particle.color)) {
      this.pendingProgress.push(progressStep * 2);
    } else {
      this.pendingProgress.push(0 - progressStep * 5);
    }
  }

  isCleared() {
    return this.progress > this.completeThreshold;
  }

  step() {
    let newProgress = this.progress - this.progressStep;
    this.pendingProgress.forEach(diff => {
      newProgress += diff;
    });
    newProgress = Math.min(100, Math.max(0, newProgress));
    this.pendingProgress = [];
    this.progress = newProgress;

    // scales it such that threshold => 100% volume
    const newVolume = Math.min(1, this.progress / this.completeThreshold);
    if (this.volume !== newVolume) {
      GlobalManager.song.setTracksVolume(this.tracks, newVolume);
    }
    this.volume = newVolume;
  }

  draw(tools: Tools) {
    const { position, progress, width, basinHeight, completeThreshold, acceptsColors } = this;
    const color = acceptsColors.length ? acceptsColors[0] : 'white';

    // draw basin
    tools.ctx.fillStyle = color;
    tools.ctx.fillRect(
      position.x - width / 2,
      GridHeight - (position.y + basinHeight),
      width,
      basinHeight
    );

    // draw progress
    const visualProgressMultiplier = 2;
    const visualProgress = progress * visualProgressMultiplier;
    tools.ctx.fillStyle = progress >= completeThreshold ? '#FFFFFF' : '#BBBBBB';
    tools.ctx.fillRect(
      position.x - width / 2,
      GridHeight - (position.y + basinHeight + visualProgress),
      width,
      visualProgress
    );

    // draw outline
    const maxHeight = this.basinHeight + 100 * visualProgressMultiplier;
    tools.ctx.lineWidth = width / 15;
    tools.ctx.strokeStyle = color;
    tools.ctx.strokeRect(
      position.x - width / 2,
      GridHeight - (position.y + maxHeight),
      width,
      maxHeight
    );
  }
}



// WEBPACK FOOTER //
// ./src/Goal.js